/**
 * Generic asset conversion page.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { INTERNAL_CONVERT_PAIRS } from '@uo/shared';
import { ApiError } from '../api/client';
import { isInternalConvertPair } from '../api/asset-conversion';
import { usersAPI } from '../api/users';
import { useAuthStore } from '../store/auth';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C, ThemedBottomActionBar, ThemedField, ThemedInput, ThemedNotice, ThemedPanel, ThemedSubmitButton, ThemedTextarea } from '../components/ui/ThemedSurface';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';
import { tokenDisplayLabel } from '@/utils/orb-display';

interface BalancesResponse {
  balances: Record<string, string>;
}

interface ConvertResult {
  ref: string;
  from_asset: string;
  to_asset: string;
  amount: string;
}

const ASSET_LABEL: Record<string, string> = {
  uo: tokenDisplayLabel('uo'),
  uoc: tokenDisplayLabel('uoc'),
  ep: 'EP',
  ep1: 'EP1',
  ep2: 'EP2',
  rp: 'RP',
  tp: 'TP',
  mp: 'MP',
  fp: 'FP',
  fc: 'FC',
  sp1: 'SP1',
  sp2: 'SP2',
  sp3: 'SP3',
};

const CONVERT_RATIO: Record<string, string> = {};

function assetLabel(key: string): string {
  return ASSET_LABEL[key.toLowerCase()] ?? key.toUpperCase();
}

function convertRatio(from: string, to: string): string {
  return CONVERT_RATIO[`${from.toLowerCase()}:${to.toLowerCase()}`] ?? '1:1';
}

function ConvertSummaryPanel({
  fromLabel,
  toLabel,
  fromBalance,
  toBalance,
  ratio,
}: {
  fromLabel: string;
  toLabel: string;
  fromBalance: string;
  toBalance: string;
  ratio: string;
}) {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="overflow-hidden p-0">
      <div className="relative px-4 py-4">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${C.gold} 50%, transparent 100%)` }} />
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-xl border px-3 py-3" style={{ borderColor: C.softBorder, background: C.inputBg }}>
          <div className="min-w-0">
            <div className="truncate text-[10px] leading-none" style={{ color: C.muted }}>{t('convert.holding')} {fromLabel}</div>
            <div className="mt-2 truncate font-mono text-xl font-semibold leading-none" style={{ color: C.gold, textShadow: `0 0 14px ${C.gold}24` }}>
              {Number(fromBalance).toFixed(2)}
            </div>
          </div>
          <div className="h-11 w-px" style={{ background: C.statDividerAlt }} />
          <div className="min-w-0 text-right">
            <div className="truncate text-[10px] leading-none" style={{ color: C.muted }}>{toLabel} {t('convert.balance')}</div>
            <div className="mt-2 truncate font-mono text-xl font-semibold leading-none" style={{ color: C.text }}>
              {Number(toBalance).toFixed(2)}
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between rounded-xl border px-3 py-2 text-xs" style={{ borderColor: C.line, background: `linear-gradient(90deg, transparent 0%, ${C.gold}10 48%, ${C.buy}10 100%)`, color: C.muted }}>
          <span>{t('convert.ratio')}</span>
          <span className="font-mono font-semibold" style={{ color: C.gold }}>{ratio}</span>
        </div>
      </div>
    </ThemedPanel>
  );
}

export function ConvertPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const profile = useAuthStore((s) => s.profile);

  const from = searchParams.get('from') ?? 'ep';
  const to = searchParams.get('to') ?? 'rp';
  const fromLabel = assetLabel(from);
  const toLabel = assetLabel(to);

  // Dedicated flows (quota / donate) — do not use generic convert.
  useEffect(() => {
    const f = from.toLowerCase();
    const tAsset = to.toLowerCase();
    if (f === 'rp' && tAsset === 'sp3') {
      navigate('/convert/rp-sp3', { replace: true });
      return;
    }
    if (f === 'fp' && tAsset === 'uo') {
      navigate('/donations', { replace: true });
    }
  }, [from, to, navigate]);

  const [amount, setAmount] = useState('');
  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [memo, setMemo] = useState('');
  const [showSecurity, setShowSecurity] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { data: balData } = useQuery({
    queryKey: ['user', 'balances'],
    queryFn: () => usersAPI.getBalances(),
    refetchInterval: 30_000,
  });

  const { data: conversionCatalog } = useQuery({
    queryKey: ['users', 'asset-conversion'],
    queryFn: () => usersAPI.getAssetConversion(),
    staleTime: 60_000,
  });

  const pairSupported =
    isInternalConvertPair(conversionCatalog, from, to) ||
    INTERNAL_CONVERT_PAIRS.some(([a, b]) => a === from.toLowerCase() && b === to.toLowerCase());

  const fromBalance = balData?.balances?.[from.toLowerCase()] ?? '0';
  const toBalance = balData?.balances?.[to.toLowerCase()] ?? '0';
  const formDisabled =
    !pairSupported ||
    !amount.trim() ||
    !txPassword.trim() ||
    !securityAnswer.trim();

  const convertMut = useMutation({
    mutationFn: (body: { from_asset: string; to_asset: string; amount: string; transaction_password: string; security_answer?: string; memo?: string }) =>
      usersAPI.convert(body) as unknown as Promise<ConvertResult>,
    onSuccess: () => {
      setSuccess(true);
      setError(null);
      setTimeout(() => navigate(-1), 1500);
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('convert.convertFailed'));
    },
  });

  function handleSubmit() {
    setError(null);
    if (!amount || Number(amount) <= 0) {
      setError(t('convert.amountError'));
      return;
    }
    if (!txPassword) {
      setError(t('common.enterTxPassword'));
      return;
    }
    convertMut.mutate({
      from_asset: from.toLowerCase(),
      to_asset: to.toLowerCase(),
      amount: amount.trim(),
      transaction_password: txPassword,
      security_answer: securityAnswer,
      memo: memo || undefined,
    });
  }

  const securityQuestion = (profile as Record<string, unknown> | null)?.['security_question'] as string | undefined;
  const questionText = securityQuestion ?? t('convert.defaultSecurityQuestion');

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={`${fromLabel} ${t('convert.titleConvert')} ${toLabel}`} onBack={() => navigate(-1)} theme={C} />
        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]" style={{ height: 'calc(100% - 70px)', paddingBottom: '12px' }}>
          <ConvertSummaryPanel
            fromLabel={fromLabel}
            toLabel={toLabel}
            fromBalance={fromBalance}
            toBalance={toBalance}
            ratio={convertRatio(from, to)}
          />

          {!pairSupported && <ThemedNotice tone="info">{t('convert.pairNotSupported')}</ThemedNotice>}

          <ThemedPanel className="p-4">
            <div className="flex flex-col gap-5">
              <ThemedField label={t('convert.amount')} required>
                <div className="relative">
                  <ThemedInput type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t('convert.placeholder')} min="0" step="any" className="pr-16" />
                  <button
                    type="button"
                    onClick={() => setAmount(fromBalance)}
                    className="absolute right-2 top-1/2 flex h-7 -translate-y-1/2 items-center px-2 text-[11px] font-semibold transition-all active:scale-[0.98]"
                    style={{ color: C.gold }}
                  >
                    {t('common.all_btn')}
                  </button>
                </div>
              </ThemedField>
              <TransactionSecurity
                txPassword={txPassword}
                onTxPasswordChange={setTxPassword}
                showSecurity={showSecurity}
                onShowSecurityChange={setShowSecurity}
                securityAnswer={securityAnswer}
                onSecurityAnswerChange={setSecurityAnswer}
                securityQuestionText={questionText}
                colors={{
                  muted: C.muted,
                  red: C.sell,
                  activeGreen: C.activeGreen,
                  gold: C.gold,
                  softBorder: C.softBorder,
                  inputBg: C.inputBg,
                }}
              />
              <ThemedField label={t('convert.memo')}>
                <ThemedTextarea value={memo} onChange={(e) => setMemo(e.target.value)} placeholder={t('convert.memo')} rows={3} />
              </ThemedField>
            </div>
          </ThemedPanel>

          {error && <ThemedNotice tone="error">{error}</ThemedNotice>}
          {success && <ThemedNotice tone="success">{t('convert.successMsg')}</ThemedNotice>}
        </div>
        <ThemedBottomActionBar background={C.bottomBarBg}>
          <ThemedSubmitButton disabled={success || convertMut.isPending || formDisabled} onClick={handleSubmit}>
            {convertMut.isPending ? '…' : t('convert.submit')}
          </ThemedSubmitButton>
        </ThemedBottomActionBar>
      </div>
    </div>
  );
}
